<template>
  <div class="animated fadeIn lunch-history-page">
    <div class="card">
      <div class="main-card-body">
        <div class="body-header history-header">
          <div>
            <h4 class="card-header-title">LỊCH SỬ ĐĂNG KÝ CƠM</h4>
            <p>Theo dõi các suất ăn đã đăng ký và đã hủy</p>
          </div>
          <button class="history-back-button" type="button" @click="goBack">
            <i class="fa fa-arrow-left"></i> Quay lại đăng ký
          </button>
        </div>

        <section class="history-card">
          <div v-if="canViewCompany" class="scope-switch">
            <button
              type="button"
              :class="{ active: filters.scope === 'me' }"
              @click="changeScope('me')"
            >
              Của tôi
            </button>
            <button
              type="button"
              :class="{ active: filters.scope === 'company' }"
              @click="changeScope('company')"
            >
              Toàn công ty
            </button>
          </div>

          <div class="history-filters">
            <div class="filter-field date-range-field">
              <label>Khoảng thời gian</label>
              <el-date-picker
                v-model="filters.dateRange"
                type="daterange"
                range-separator="-"
                start-placeholder="Từ ngày"
                end-placeholder="Đến ngày"
                format="dd-MM-yyyy"
                value-format="yyyy-MM-dd"
                :clearable="false"
              />
            </div>
            <div class="filter-field">
              <label>Trạng thái</label>
              <el-select v-model="filters.status">
                <el-option label="Tất cả" value="" />
                <el-option label="Đã đăng ký" value="REGISTERED" />
                <el-option label="Đã hủy" value="CANCELLED" />
              </el-select>
            </div>
            <div v-if="filters.scope === 'company'" class="filter-field">
              <label>Phòng ban</label>
              <el-select v-model="filters.departmentId" filterable clearable placeholder="Tất cả">
                <el-option
                  v-for="department in departments"
                  :key="department.id"
                  :label="department.name"
                  :value="department.id"
                />
              </el-select>
            </div>
            <div v-if="filters.scope === 'company'" class="filter-field employee-search-field">
              <label>Nhân viên</label>
              <el-input
                v-model.trim="filters.search"
                clearable
                placeholder="Mã hoặc tên nhân viên"
                @keyup.enter.native="applyFilters"
              />
            </div>
            <div class="filter-actions">
              <el-button type="primary" icon="el-icon-search" @click="applyFilters">Tìm kiếm</el-button>
            </div>
          </div>

          <el-table
            v-loading="loading"
            :data="registrations"
            :max-height="560"
            border
            stripe
            empty-text="Chưa có lịch sử đăng ký cơm trong khoảng thời gian này."
            @row-click="openDetail"
          >
            <el-table-column prop="date" label="Ngày ăn" width="120">
              <template slot-scope="scope">{{ formatDate(scope.row.date) }}</template>
            </el-table-column>
            <el-table-column
              v-if="filters.scope === 'company'"
              label="Nhân viên"
              min-width="190"
            >
              <template slot-scope="scope">
                <strong>{{ scope.row.employeeName || "-" }}</strong>
                <small class="employee-meta">{{ scope.row.employeeNo || "-" }}</small>
              </template>
            </el-table-column>
            <el-table-column
              v-if="filters.scope === 'company'"
              prop="departmentName"
              label="Phòng ban"
              min-width="150"
            />
            <el-table-column label="Món ăn" min-width="230">
              <template slot-scope="scope">{{ formatFoods(scope.row.items) }}</template>
            </el-table-column>
            <el-table-column prop="note" label="Ghi chú" min-width="160">
              <template slot-scope="scope">{{ scope.row.note || "-" }}</template>
            </el-table-column>
            <el-table-column label="Tổng tiền" width="130" align="right">
              <template slot-scope="scope">{{ formatCurrency(scope.row.totalAmount) }}</template>
            </el-table-column>
            <el-table-column label="Trạng thái" width="130" align="center">
              <template slot-scope="scope">
                <span class="status-pill" :class="statusClass(scope.row.status)">
                  {{ statusLabel(scope.row.status) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column label="Cập nhật lúc" width="160">
              <template slot-scope="scope">{{ formatDateTime(scope.row.updated_at) }}</template>
            </el-table-column>
          </el-table>

          <div v-if="total > 0" class="history-pagination">
            <span>Tổng cộng {{ total }} đăng ký</span>
            <el-pagination
              background
              layout="prev, pager, next, sizes"
              :current-page="page"
              :page-size="pageSize"
              :page-sizes="[10, 20, 50, 100]"
              :total="total"
              @current-change="changePage"
              @size-change="changePageSize"
            />
          </div>
        </section>
      </div>
    </div>

    <el-dialog
      title="Chi tiết đăng ký cơm"
      :visible.sync="detailVisible"
      width="560px"
      custom-class="lunch-history-dialog"
      append-to-body
    >
      <div v-if="selectedRegistration" class="history-detail">
        <div v-if="filters.scope === 'company'" class="detail-heading">
          <strong>{{ selectedRegistration.employeeNo }} - {{ selectedRegistration.employeeName }}</strong>
          <span>{{ selectedRegistration.departmentName || "Chưa xác định" }}</span>
        </div>
        <div class="detail-row">
          <span>Ngày ăn</span>
          <strong>{{ formatDate(selectedRegistration.date) }}</strong>
        </div>
        <div
          v-for="(item, index) in selectedRegistration.items"
          :key="`${item.foodId || index}-${index}`"
          class="detail-food"
        >
          <div>
            <strong>{{ item.foodName || "Món ăn" }}</strong>
            <span>{{ formatCurrency(item.price) }} × {{ item.quantity }}</span>
          </div>
          <strong>{{ formatCurrency(item.total || item.price * item.quantity) }}</strong>
        </div>
        <div class="detail-row">
          <span>Ghi chú</span>
          <strong>{{ selectedRegistration.note || "-" }}</strong>
        </div>
        <div class="detail-row">
          <span>Trạng thái</span>
          <span class="status-pill" :class="statusClass(selectedRegistration.status)">
            {{ statusLabel(selectedRegistration.status) }}
          </span>
        </div>
        <div class="detail-row">
          <span>Thời điểm đăng ký</span>
          <strong>{{ formatDateTime(selectedRegistration.created_at) }}</strong>
        </div>
        <div class="detail-row">
          <span>Cập nhật gần nhất</span>
          <strong>{{ formatDateTime(selectedRegistration.updated_at) }}</strong>
        </div>
        <div v-if="selectedRegistration.cancelled_at" class="detail-row">
          <span>Thời điểm hủy</span>
          <strong>{{ formatDateTime(selectedRegistration.cancelled_at) }}</strong>
        </div>
        <div class="detail-total">
          <span>Tổng tiền</span>
          <strong>{{ formatCurrency(selectedRegistration.totalAmount) }}</strong>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="detailVisible = false">Đóng</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import MethodService from "@/service/MethodService";
import LunchApi from "./lunchApiService";

function toDateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function defaultDateRange() {
  const today = new Date();
  return [toDateKey(new Date(today.getFullYear(), today.getMonth(), 1)), toDateKey(today)];
}

export default {
  name: "LunchRegistrationHistory",
  data() {
    return {
      loading: false,
      registrations: [],
      departments: [],
      total: 0,
      page: 1,
      pageSize: 20,
      loadRequestId: 0,
      detailVisible: false,
      selectedRegistration: null,
      filters: {
        scope: "me",
        dateRange: defaultDateRange(),
        status: "",
        departmentId: "",
        search: ""
      }
    };
  },
  computed: {
    canViewCompany() {
      return MethodService.checkHaveFeature("FT2403");
    }
  },
  created() {
    this.loadHistory();
    if (this.canViewCompany) this.loadDepartments();
  },
  methods: {
    async loadHistory() {
      const requestId = ++this.loadRequestId;
      this.loading = true;
      try {
        const range = this.filters.dateRange || [];
        const response = await LunchApi.listRegistrationHistory({
          scope: this.filters.scope,
          fromDate: range[0],
          toDate: range[1],
          status: this.filters.status,
          departmentId: this.filters.scope === "company" ? this.filters.departmentId : "",
          search: this.filters.scope === "company" ? this.filters.search : "",
          limit: this.pageSize,
          offset: (this.page - 1) * this.pageSize
        });
        if (requestId !== this.loadRequestId) return;
        this.registrations = response.result || [];
        this.total = Number(response.meta.total) || 0;
      } catch (error) {
        if (requestId !== this.loadRequestId) return;
        this.registrations = [];
        this.total = 0;
        this.showApiError(error, "Không tải được lịch sử đăng ký cơm.");
      } finally {
        if (requestId === this.loadRequestId) this.loading = false;
      }
    },
    async loadDepartments() {
      try {
        const departments = await LunchApi.listDepartments();
        this.departments = (departments || [])
          .filter(item => item && item.id)
          .map(item => ({ id: item.id, name: item.name || item.code || "Chưa xác định" }))
          .sort((a, b) => a.name.localeCompare(b.name));
      } catch (error) {
        this.departments = [];
      }
    },
    changeScope(scope) {
      if (scope === "company" && !this.canViewCompany) return;
      this.filters.scope = scope;
      this.filters.departmentId = "";
      this.filters.search = "";
      this.page = 1;
      this.loadHistory();
    },
    applyFilters() {
      this.page = 1;
      this.loadHistory();
    },
    changePage(page) {
      this.page = page;
      this.loadHistory();
    },
    changePageSize(size) {
      this.pageSize = size;
      this.page = 1;
      this.loadHistory();
    },
    openDetail(registration) {
      this.selectedRegistration = registration;
      this.detailVisible = true;
    },
    goBack() {
      this.$router.push({ path: "/admin/lunch-registration" }).catch(() => {});
    },
    formatFoods(items) {
      if (!items || !items.length) return "-";
      return items.map(item => `${item.foodName || "Món ăn"} × ${item.quantity}`).join(", ");
    },
    formatDate(value) {
      if (!value) return "-";
      const parts = String(value).split("-");
      return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : value;
    },
    formatDateTime(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "-";
      return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()} ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
    },
    formatCurrency(value) {
      return `${Number(value || 0).toLocaleString("vi-VN")} đ`;
    },
    statusLabel(status) {
      return status === "CANCELLED" ? "Đã hủy" : "Đã đăng ký";
    },
    statusClass(status) {
      return status === "CANCELLED" ? "status-cancelled" : "status-registered";
    },
    showApiError(error, fallback) {
      this.$message({
        type: "error",
        message: (error && error.message) || fallback,
        duration: 3000,
        showClose: true
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.lunch-history-page {
  color: #11264d;
  font-family: UTM Avo;
  font-size: 14px;
}
.history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0;
  margin-bottom: 16px;
}
.history-header h4 {
  margin: 0;
  color: #0d2248;
  font-size: 20px;
  font-weight: 700;
}
.history-header p {
  margin: 5px 0 0;
  color: #68758b;
}
.history-back-button {
  min-height: 40px;
  border: 1px solid #ff9d5c;
  border-radius: 7px;
  background: #fff;
  color: #e76531;
  cursor: pointer;
  font: inherit;
  padding: 8px 14px;
}
.history-card {
  border: 1px solid #e3e9f1;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 4px 18px rgba(17, 38, 77, 0.05);
  padding: 16px;
}
.scope-switch {
  display: inline-flex;
  border: 1px solid #ff9d5c;
  border-radius: 7px;
  overflow: hidden;
  margin-bottom: 16px;
}
.scope-switch button {
  min-width: 110px;
  border: 0;
  background: #fff;
  color: #e76531;
  cursor: pointer;
  font: inherit;
  padding: 9px 14px;
}
.scope-switch button.active {
  background: #ff9d5c;
  color: #fff;
}
.history-filters {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}
.filter-field {
  display: grid;
  gap: 6px;
  min-width: 170px;
}
.filter-field label {
  color: #53627a;
  font-size: 13px;
  font-weight: 600;
}
.date-range-field {
  min-width: 330px;
}
.employee-search-field {
  min-width: 220px;
}
.filter-actions {
  display: flex;
  gap: 8px;
}
::v-deep .filter-actions .el-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 42px;
  padding: 0 16px;
  white-space: nowrap;
}
.employee-meta {
  display: block;
  color: #77849a;
  margin-top: 3px;
}
.status-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  padding: 5px 10px;
  white-space: nowrap;
}
.status-registered {
  background: #e8f8ef;
  color: #079653;
}
.status-cancelled {
  background: #fff0ef;
  color: #db3f36;
}
.history-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: #68758b;
  margin-top: 16px;
}
.history-detail,
.detail-heading,
.detail-food > div {
  display: grid;
  gap: 4px;
}
.detail-heading {
  border-bottom: 1px solid #e7ebf1;
  margin-bottom: 10px;
  padding-bottom: 12px;
}
.detail-heading span,
.detail-food span {
  color: #68758b;
  font-size: 13px;
}
.detail-row,
.detail-food,
.detail-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 1px solid #eef1f5;
  padding: 11px 0;
}
.detail-row > span:first-child,
.detail-total span {
  color: #68758b;
}
.detail-total {
  border-bottom: 0;
  font-size: 16px;
}
.detail-total strong {
  color: #0d2248;
  font-size: 19px;
}
::v-deep .el-table__row {
  cursor: pointer;
}
::v-deep .el-button--primary {
  border-color: #ff9d5c;
  background: #ff9d5c;
}
::v-deep .date-range-field .el-date-editor,
::v-deep .filter-field .el-select,
::v-deep .filter-field .el-input {
  width: 100%;
}
@media (max-width: 767px) {
  .history-header,
  .history-pagination {
    align-items: stretch;
    flex-direction: column;
  }
  .history-back-button,
  .filter-field,
  .date-range-field,
  .employee-search-field,
  .filter-actions {
    width: 100%;
    min-width: 0;
  }
  .filter-actions .el-button {
    flex: 1;
  }
  .history-pagination {
    overflow-x: auto;
  }
}
</style>
